<script setup>
import IndexLayout from "@/Layouts/IndexLayout.vue";
import {computed, ref, watch} from "vue";
import Filters from "@/Pages/Product/Components/Filters.vue";
import SearchAndSort from "@/Pages/Product/Components/SearchAndSort.vue";
import Offer from "@/Pages/Product/Components/Offer.vue";
import PaginateItems from "@/Components/PaginateItems.vue";
import CardProductHeader from "@/Pages/Product/Components/CardProductHeader.vue";
import {Head, router, usePage} from "@inertiajs/vue3";
import debounce from "lodash-es/debounce";
import SearchResultEmpty from "@/Components/SearchResultEmpty.vue";
import Card from "@/Components/Card.vue";
import {route} from "ziggy-js";
import {trans} from "laravel-vue-i18n";

const page = usePage()
const product = page.props.product
const offers = ref(page.props.offers.data)
const currentPage = ref(1)
const isLoading = ref(false)
const search = ref(route().params?.search ?? undefined)
const lot_id = ref(route().params?.lot_id ? Number(route().params.lot_id) : 'all')
const lots_active = ref(page.props.product.lots_active)
const propertyValues = ref(route().params?.properties ?? undefined)
const selectedLot = computed(() => page.props.product.lots_active.find((el) => (el.id === lot_id.value)))
const sellerOnline = ref(Boolean(route().params?.seller_online))
const properties = ref(page.props.properties)
const arrSort = computed(() => {
    return [{id: 0, title: trans('Newest'), val: 'updated_at_desc'}, {
        id: 1,
        title: trans('Oldest'),
        val: 'updated_at_asc'
    },
        {id: 2, title: trans('Price increasing'), val: 'price_asc'},
        {id: 3, title: trans('Price decreasing'), val: 'price_desc'}]
})
const offers_sort = ref(route().params['offers_sort'] ? arrSort.value.find(item => item.val === route().params['offers_sort']) : arrSort.value[0])


const pageTitle = computed(() => {
    const activeLot = lots_active.value.find(lot => lot.id === lot_id.value)
    if (activeLot) {
        return trans('Buy :lot_title :product_title at low price on AnyKod. Guaranteed deal, verified sellers.', {
            lot_title: activeLot.title,
            product_title: product.title
        })
    } else {
        return trans('Buy :lot_title :product_title at low price on AnyKod. Guaranteed deal, verified sellers.', {
            lot_title: '',
            product_title: product.title
        })
    }
})

const getPropertyValuesFetch = () => {
    let newObj = {};
    for (let key in propertyValues.value) {
        if (propertyValues.value[key].value) {
            newObj[key] = propertyValues.value[key];
            if (typeof propertyValues.value[key].value === 'boolean') {
                newObj[key]['value'] = propertyValues.value[key].value ? 1 : 0;
            }
        } else if (propertyValues.value[key].value_from || propertyValues.value[key].value_to) {
            newObj[key] = propertyValues.value[key];
            if (!propertyValues.value[key].value_from) {
                delete newObj[key]['value_from']
            }
            if (!propertyValues.value[key].value_to) {
                delete newObj[key]['value_to']
            }
        }


    }
    return Object.keys(newObj)?.length > 0 ? newObj : undefined;
}

const getQueryParams = (nextPage = false) => {
    return {
        search: search.value ? search.value : undefined,
        lot_id: lot_id.value !== 'all' ? lot_id.value : undefined,
        seller_online: sellerOnline.value ? 1 : undefined,
        properties: lot_id.value === Number(route().params?.lot_id) ? getPropertyValuesFetch() : undefined,
        offers_sort: offers_sort.value ? offers_sort.value.val : undefined,
        page: (nextPage && currentPage.value) ? currentPage.value : 1
    }
}



const fetch = (nextPage = false) => {
    if (!nextPage) {
        currentPage.value = 1
    }

    const queryParams = getQueryParams(nextPage)

    if (lot_id.value !== Number(route().params?.lot_id)) {
        // properties.value = lots_active.value.find(lot => lot.id === lot_id.value)?.properties ?? undefined
        setPropertyValues()
    }

    isLoading.value = true
    router.get(route('products.show', {product: product.id}), queryParams,
        {
            preserveState: true,
            preserveScroll: true,
            only: ['offers', 'properties'],
            onFinish: () => {
                if (nextPage) {
                    offers.value = [...offers.value, ...page.props.offers.data]
                } else {
                    offers.value = page.props.offers.data
                }
                properties.value = page.props.properties
                setPropertyValues()
                isLoading.value = false
            },
        })


}
const fetchDebounced = debounce(fetch, 1000)
const hasMore = computed(() => {
    return currentPage.value < page.props.offers.last_page
})
const onShowMore = () => {
    currentPage.value++
    fetch(true)
}

watch([lot_id, sellerOnline, offers_sort], () => {
    fetch(false)
}, {deep: true})

watch(search, () => {
    fetchDebounced(false)
})


const updateFilter = (event) => {
    propertyValues.value = event
    fetchDebounced(false)
}


const clearFilters = () => {
    createPropertyValues()
    fetch(false)
}

const createPropertyValues = () => {
    propertyValues.value = null
    propertyValues.value = properties.value?.reduce((acc, property) => {
        if (property.type === 'int' || property.type === 'float') {
            acc[property.id] = {
                id: property.id,
                value_from: null,
                value_to: null
            }
        } else {
            acc[property.id] = {
                id: property.id,
                value: property.type === 'bool' ? false : null
            }
        }
        return acc;
    }, {});
}

const setPropertyValues = () => {
    createPropertyValues()
    if (lot_id.value === Number(route().params?.lot_id)) {
        let routeProperties = route().params.properties;
        if (routeProperties) {
            Object.keys(routeProperties).forEach(item => {
                propertyValues.value[routeProperties[item].id] = routeProperties[item]
                propertyValues.value[routeProperties[item].id].id = Number(propertyValues.value[routeProperties[item].id].id)
                if (typeof propertyValues.value[routeProperties[item].id].value !== 'undefined') {
                    propertyValues.value[routeProperties[item].id].value = /^[0-9]+$/.test(propertyValues.value[routeProperties[item].id].value)
                        ? Number(propertyValues.value[routeProperties[item].id].value)
                        : propertyValues.value[routeProperties[item].id].value
                }
            })
        }

    }
}
setPropertyValues()
</script>

<template>
    <IndexLayout :title="pageTitle">
        <Head>
            <meta :content="product.description" name="description"/>
        </Head>
        <div
            class=" mx-auto lg:px-0 py-2.5 lg:py-6  px-2 sm:px-0 sm:max-w-[38.75rem]  lg:max-w-[62rem] xl:max-w-[78rem] 2xl:max-w-[87.5rem]">
            <div class="flex items-start justify-center gap-2.5">
                <Filters v-model:lot-id="lot_id"
                         v-model:property-values="propertyValues"
                         @update-property-values="updateFilter($event)"
                         :have-property-value="Boolean(getPropertyValuesFetch())"
                         :properties="properties"
                         :lots-active="lots_active"
                         @clear-filters="clearFilters()"
                         class="hidden lg:flex w-[22.25rem] pr-4 sticky top-2 border-r border-dashed border-slate-300 dark:border-slate-700 pb-8"/>
                <div class="grow flex flex-col gap-2.5 w-full">
                    <CardProductHeader :product="product" :selected-lot="selectedLot"></CardProductHeader>
                    <Filters v-model:lot-id="lot_id"
                             v-model:property-values="propertyValues"
                             @update-property-values="updateFilter($event)"
                             :have-property-value="Boolean(getPropertyValuesFetch())"
                             :properties="page.props.properties"
                             :lots-active="lots_active"
                             @clear-filters="clearFilters()"
                             class="flex lg:hidden w-full"/>
                    <SearchAndSort :loading="isLoading"
                                   :arr-sort="arrSort"
                                   v-model:seller-online="sellerOnline"
                                   v-model:sort="offers_sort"
                                   v-model:search="search"/>
                    <template v-if="offers.length > 0">
                        <template v-for="offer in offers">
                            <Offer :data="offer" :loading="isLoading"/>
                        </template>
                        <PaginateItems class="-mt-2"
                                       :on-show-more="onShowMore"
                                       :has-more="hasMore">
                        </PaginateItems>
                    </template>
                    <template v-else>
                        <Card class="p-2.5 relative" :class="{'loading': isLoading}">
                            <SearchResultEmpty in-header :lot-name="selectedLot?.title" :product-id="product.id"
                                               :product-name="product.title"/>
                        </Card>
                        <template v-if="!offers.value && (lot_id !== 'all' || !lot_id)">
                            <h3 class="font-sans font-medium dark:text-slate-200 text-xl px-1 py-3">
                                {{$t('You may be interested:')}}
                            </h3>
                            <template v-for="offer in $page.props.more_offers.data">
                                <Offer :data="offer" :loading="isLoading"/>
                            </template>
                            <PaginateItems class="-mt-2"
                                           :on-show-more="onShowMore"
                                           :has-more="hasMore">
                            </PaginateItems>
                        </template>

                    </template>

                </div>
            </div>
        </div>
    </IndexLayout>
</template>


